import { useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar,
} from 'recharts';
import { useAuth } from '@/hooks/useAuth';
import { Download, Building2 } from 'lucide-react';
import { generateSampleTrendReportPDF } from '@/lib/pdfGenerator';
import type { SavedSample } from '../types/calculator';

interface SampleReportPrintProps {
  reportNumber: string;
  reportTitle: string;
  samples: SavedSample[];
  createdAt: string | Date;
  /** Customer / client name */
  customerName?: string;
  /** Discharge site name */
  siteName?: string;
  /** Discharge site address */
  siteAddress?: string;
  /** Permit / consent reference */
  permitRef?: string;
  /** Regulatory authority name */
  regulatoryAuthority?: string;
  /** Discharge standard code */
  standard?: string;
}

export default function SampleReportPrint({
  reportNumber, reportTitle, samples, createdAt,
  customerName, siteName, siteAddress, permitRef, regulatoryAuthority, standard,
}: SampleReportPrintProps) {
  const { user } = useAuth();
  const sortedSamples = useMemo(() =>
    [...samples].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
  [samples]);

  const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

  const chartData = useMemo(() =>
    sortedSamples.map(s => ({
      date: new Date(s.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
      fullDate: new Date(s.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }),
      name: s.name,
      BOD: safeNum(s.params?.bod5),
      COD: safeNum(s.params?.cod),
      TSS: safeNum(s.params?.tss),
      pH: safeNum(s.params?.ph),
      TDS: safeNum(s.params?.tds),
      TKN: safeNum(s.params?.tkn),
      TP: safeNum(s.params?.totalPhosphorus),
    })),
  [sortedSamples]);

  const reportDate = new Date(createdAt);
  const formattedDate = reportDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const formattedTime = reportDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const authorName = user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.name || user?.username || 'Not specified';

  const handleDownloadPDF = () => {
    const pdfSamples = sortedSamples.map(s => ({
      name: s.name,
      date: new Date(s.date),
      params: s.params as Record<string, number>,
      reportNumber: (s as any).reportNumber,
    }));
    generateSampleTrendReportPDF(pdfSamples, {
      reportNumber,
      reportTitle,
      customerName,
      siteName,
      siteAddress,
      permitRef,
      regulatoryAuthority,
      standard,
      preparedBy: authorName !== 'Not specified' ? authorName : undefined,
      createdAt: new Date(createdAt),
    });
  };

  return (
    <div className="space-y-6 print-sample-report">
      {/* PDF Download Button */}
      <div className="flex justify-end">
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-rb-active text-white font-bold hover:bg-rb-surface transition-all shadow-md"
        >
          <Download className="w-4 h-4 text-rb-brand" /> Download Regulatory PDF
        </button>
      </div>

      {/* Report Header */}
      <div className="rb-card p-6 border border-slate-800/50">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Building2 className="w-4 h-4 text-rb-brand" />
              <span className="text-[10px] text-rb-brand font-bold uppercase tracking-widest">Reynolds & Bauhm</span>
            </div>
            <h1 className="text-xl font-bold text-white">Wastewater Sample Trend Analysis</h1>
            <p className="text-sm text-slate-500 mt-1">{reportTitle}</p>
          </div>
          <div className="text-right">
            <div className="inline-block px-4 py-2.5 rounded-xl bg-rb-brand/10 border border-rb-brand/20">
              <div className="text-[10px] text-rb-brand font-bold uppercase tracking-wider">Report Number</div>
              <div className="text-base font-bold text-rb-brand font-mono">{reportNumber}</div>
            </div>
            <div className="text-xs text-slate-500 mt-2">Generated: {formattedDate} at {formattedTime}</div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-800/50 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
          <span>Samples: <strong className="text-white">{samples.length}</strong></span>
          <span>Date Range: <strong className="text-white">{chartData[0]?.fullDate} — {chartData[chartData.length - 1]?.fullDate}</strong></span>
          {customerName && <span>Client: <strong className="text-white">{customerName}</strong></span>}
          <span>Author: <strong className="text-white">{authorName}</strong></span>
        </div>
        {/* Regulatory / Site block */}
        {(siteName || permitRef || regulatoryAuthority || standard) && (
          <div className="mt-3 pt-3 border-t border-slate-800/50 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs text-slate-500">
            {siteName && <span>Discharge Site: <strong className="text-white">{siteName}</strong></span>}
            {siteAddress && <span>Location: <strong className="text-white">{siteAddress}</strong></span>}
            {permitRef && <span>Permit Ref: <strong className="text-white">{permitRef}</strong></span>}
            {regulatoryAuthority && <span>Regulatory Authority: <strong className="text-white">{regulatoryAuthority}</strong></span>}
            {standard && <span>Discharge Standard: <strong className="text-rb-brand">{standard}</strong></span>}
          </div>
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rb-card p-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">BOD, COD & TSS Over Time</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <Tooltip contentStyle={{ backgroundColor: 'var(--rb-elevated)', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)' }} itemStyle={{ color: 'var(--rb-text)' }} />
                <Legend wrapperStyle={{ color: 'var(--rb-text-2)', fontSize: 'var(--rb-font-xs-)' }} />
                <Line type="monotone" dataKey="BOD" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: '#22d3ee' }} />
                <Line type="monotone" dataKey="COD" stroke="var(--rb-brand)" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-brand)' }} />
                <Line type="monotone" dataKey="TSS" stroke="#64748b" strokeWidth={2} dot={{ r: 3, fill: 'var(--rb-text-3)' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rb-card p-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">pH & Nutrients Over Time</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="left" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: 'var(--rb-text-3)', fontSize: 10 }} stroke="#334155" />
                <Tooltip contentStyle={{ backgroundColor: 'var(--rb-elevated)', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)' }} itemStyle={{ color: 'var(--rb-text)' }} />
                <Legend wrapperStyle={{ color: 'var(--rb-text-2)', fontSize: 'var(--rb-font-xs-)' }} />
                <Bar yAxisId="left" dataKey="pH" fill="var(--rb-brand)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="TKN" fill="var(--rb-active)" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="right" dataKey="TP" fill="#64748b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="rb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800/60 border-b border-slate-700">
                {['Sample', 'Date', 'BOD', 'COD', 'TSS', 'pH', 'TDS', 'TKN', 'TP'].map(h => (
                  <th key={h} className="py-2.5 px-3 font-bold text-slate-400 uppercase text-[10px] tracking-wider text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {chartData.map((row, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                  <td className="py-2.5 px-3 font-semibold text-white">{row.name}</td>
                  <td className="py-2.5 px-3 text-slate-500">{row.fullDate}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-cyan-400">{row.BOD.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-rb-brand">{row.COD.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TSS.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-white">{row.pH.toFixed(2)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TDS.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TKN.toFixed(0)}</td>
                  <td className="py-2.5 px-3 text-right font-mono text-slate-400">{row.TP.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-slate-800/50 text-center text-xs text-slate-500">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Building2 className="w-4 h-4 text-rb-brand" />
          <span className="font-bold text-white text-sm">Reynolds &amp; Bauhm</span>
        </div>
        <div className="text-slate-500">Water Treatment Engineering</div>
        <div className="mt-2 text-[10px] text-slate-600 font-mono">
          Report: {reportNumber} | Generated {formattedDate} {formattedTime} | Author: {authorName}
        </div>
        {/* Legal disclaimer */}
        <div className="mt-3 pt-3 border-t border-slate-800/40 text-left space-y-1">
          <p className="text-[10px] text-slate-500 leading-relaxed">
            <strong className="text-slate-400">Confidential.</strong> This document has been prepared solely for{' '}
            {customerName || 'the named client'} by Reynolds &amp; Bauhm Ltd and must not be reproduced or
            distributed to third parties without prior written consent.
          </p>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            All analytical results are based on sample data provided and are indicative only. Results must be
            validated by a UKAS/NATA/EPA-accredited laboratory before any regulatory submission or engineering
            decision.{' '}
            {standard
              ? `Discharge compliance is assessed against the applicable ${standard} standard.`
              : 'Site-specific consent conditions must be independently verified.'}
            {' '}Reynolds &amp; Bauhm Ltd accepts no liability for decisions made solely on the basis of this report.
          </p>
          <p className="text-[10px] text-slate-600 font-mono">
            Ref: {reportNumber} · Issue Date: {formattedDate} · Status: PRELIMINARY — NOT FOR REGULATORY SUBMISSION
          </p>
        </div>
      </div>
    </div>
  );
}
